// sessionDedup.js — May 9 2026 (v2.5.93)
// ──────────────────────────────────────────────────────────────────
// Dedup helper for mini-game session siblings.
//
// BACKGROUND
//   Mini-game sessions work by spawning sibling games tied to a root
//   game via parent_game_id. Each sibling has its game_time bumped
//   by a few seconds for stable sort. The frontend's `formatTime`
//   helper renders only hh:mm — the seconds offset is invisible —
//   so siblings of the same session look identical on screen
//   (same date, same arena, same "9:00 PM"). When two cards
//   appear in a list view, users perceive a duplicate.
//
//   Normally the spawn flow marks the parent status='completed'
//   atomically, so only the active sibling is upcoming. But if a
//   parent's status is edited by hand, or for any other reason
//   both end up in the same logical bucket, lists render the same
//   game twice.
//
// CHOICE OF "BEST" SIBLING
//   v2.5.87's first pass picked the latest game_time, which is right
//   for the dashboard's "what's next" view. But in a coach's screenshot
//   showing parent (completed, real score 1-0) + sibling (upcoming,
//   no score), the latest-time pick returned the empty upcoming row
//   and threw away the meaningful completed one. That's wrong for
//   the schedule.
//
//   New rule, in priority order:
//     1. **Status preference** — if the listing is "past games" /
//        "completed only," prefer completed siblings. If listing
//        is "upcoming," prefer upcoming siblings.
//     2. **Score presence** — within the same status bucket, prefer
//        a row with a real (non-null) home_score & away_score.
//     3. **Latest game_time** — finalfallback. Matches the active
//        mini-game in a session that's still being scored.
//
//   Callers pass an optional `prefer` argument: 'completed',
//   'upcoming', or 'latest'. Default is 'latest' (v2.5.87 behavior,
//   correct for the dashboard's mixed-status upcoming list).
// ──────────────────────────────────────────────────────────────────

/**
 * Group games by session root (parent_game_id || id) and pick one
 * representative per group.
 *
 * @param {Array<Object>} games  Array of game objects with at least
 *                               { id, parent_game_id, game_time,
 *                                 status, home_score, away_score }.
 * @param {string} [prefer='latest']  'completed' | 'upcoming' | 'latest'
 * @returns {Array<Object>}  Filtered list, one row per session root.
 */
export function dedupBySession(games, prefer = 'latest') {
  const bySession = new Map();
  for (const g of games) {
    const root = g.parent_game_id || g.id;
    const existing = bySession.get(root);
    if (!existing) {
      bySession.set(root, g);
      continue;
    }
    if (better(g, existing, prefer)) {
      bySession.set(root, g);
    }
  }
  return Array.from(bySession.values());
}

/**
 * Returns true if `candidate` is a better representative of its
 * session than `existing`, given the caller's preference.
 */
function better(candidate, existing, prefer) {
  // Step 1: status preference. Only applied for the explicit
  // 'completed'/'upcoming' modes — under the default 'latest', we
  // want pure latest-game_time wins regardless of status (matches
  // v2.5.87's dashboard behavior).
  if (prefer === 'completed') {
    const candC = candidate.status === 'completed';
    const exisC = existing.status  === 'completed';
    if (candC && !exisC) return true;
    if (!candC && exisC) return false;
  } else if (prefer === 'upcoming') {
    // Anything not 'completed' or 'cancelled' counts as upcoming
    // here. Status enum has 'upcoming' and a few transient values.
    const candU = candidate.status !== 'completed' && candidate.status !== 'cancelled';
    const exisU = existing.status  !== 'completed' && existing.status  !== 'cancelled';
    if (candU && !exisU) return true;
    if (!candU && exisU) return false;
  }

  // Step 2: score presence — also gated on a non-'latest' preference.
  // Under 'latest', score presence is irrelevant; we just want the
  // freshest sibling (the v2.5.87 contract, which keeps allGames
  // dedup deterministic and matches the dashboard tooltip).
  if (prefer !== 'latest') {
    const candHasScore = candidate.home_score != null && candidate.away_score != null;
    const exisHasScore = existing.home_score  != null && existing.away_score  != null;
    if (candHasScore && !exisHasScore) return true;
    if (!candHasScore && exisHasScore) return false;
  }

  // Step 3: latest game_time wins. String compare works because
  // game_time is a fixed HH:MM[:SS] format. Tie-break: prefer
  // the sibling (i.e., the one with parent_game_id set) over the
  // root, so the active mini stays visible.
  const candTime = candidate.game_time || '';
  const exisTime = existing.game_time  || '';
  if (candTime > exisTime) return true;
  if (candTime < exisTime) return false;
  // Equal times — prefer sibling.
  return !!candidate.parent_game_id && !existing.parent_game_id;
}
